import { m as motion, useReducedMotion } from "framer-motion";
import { useRef } from "react";

const milestones = [
  {
    year: "2020",
    title: "Norrhavet startas",
    body: "Tre vänner med en idé: marknadsföring ska vara hantverk, inte hyllning.",
  },
  {
    year: "2022",
    title: "Första Pulse-kunden",
    body: "Garageportexperten testar månadsabonnemang. Föds en modell.",
  },
  {
    year: "2023",
    title: "Fenny tar form",
    body: "Filmaffären växer ur Norrhavet — Fenny blir egen identitet, egen rytm.",
  },
  {
    year: "2024",
    title: "Premium på prenumeration",
    body: "Pulse + Prime + Pilot — tre tydliga vägar in. 30+ kunder på rytmen.",
  },
  {
    year: "2026",
    title: "Idag",
    body: "Ett team. En modell. En tydlig löfte: film varje vecka, utan drama.",
  },
];

/** Vertical scroll-driven origin story with sticky year + milestone reveals. */
export function OriginScroll() {
  const ref = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();

  return (
    <section ref={ref} className="relative py-24 md:py-32">
      <div className="container-wide">
        <div className="mb-16 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] text-peach">Resan hit</div>
          <h2 className="mt-4 text-balance text-4xl font-semibold leading-tight md:text-6xl">
            Sex år. Ett enda löfte.
          </h2>
        </div>

        <div className="relative grid gap-12 lg:grid-cols-[1fr_2fr]">
          {/* Sticky year label */}
          <div className="hidden lg:block">
            <div className="sticky top-32">
              <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                Tidslinje
              </div>
              <div
                className="mt-4 font-serif italic text-butter"
                style={{ fontSize: "clamp(3rem, 8vw, 6rem)", lineHeight: 1 }}
              >
                2020<span className="text-muted-foreground/40"> → </span>2026
              </div>
              <p className="mt-6 max-w-sm text-pretty text-base text-muted-foreground">
                Från idé till modell till rytm. Här är hur vi kom hit.
              </p>
            </div>
          </div>

          {/* Milestones */}
          <ol className="relative space-y-12 md:space-y-16">
            <div
              aria-hidden
              className="absolute left-[15px] top-3 bottom-3 w-px bg-gradient-to-b from-peach/40 via-white/10 to-transparent md:left-[19px]"
            />
            {milestones.map((m, i) => (
              <motion.li
                key={m.year}
                initial={reduce ? false : { opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                className="relative pl-12 md:pl-16"
              >
                <span className="absolute left-0 top-1.5 grid h-8 w-8 place-items-center rounded-full border border-peach/40 bg-background text-xs font-mono text-peach md:h-10 md:w-10 md:text-sm">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="text-xs uppercase tracking-widest text-peach">{m.year}</div>
                <h3 className="mt-2 text-balance text-2xl font-semibold leading-tight md:text-3xl">
                  {m.title}
                </h3>
                <p className="mt-3 max-w-xl text-pretty text-base leading-relaxed text-muted-foreground md:text-lg">
                  {m.body}
                </p>
              </motion.li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
